import { useMemo } from 'react';

import { PageShell } from '@/components/layout/page-shell';
import { PracticeCard } from '@/components/feature/practice-card';
import { useHistory } from '@/hooks/use-history';
import type { TodayPracticeCard } from '@/types/api';
import { formatDate } from '@/lib/format';

interface DayGroup {
  key: string;
  label: string;
  items: TodayPracticeCard[];
}

// groupByLocalDay buckets sessions into local-TZ days using session_date.
// The API returns newest-first and we preserve that order within each group.
function groupByLocalDay(items: TodayPracticeCard[]): DayGroup[] {
  const groups: DayGroup[] = [];
  const index = new Map<string, DayGroup>();
  for (const item of items) {
    const d = new Date(item.session_date);
    // Use local Y-M-D for the bucket key so "today" in the student's
    // timezone lands in one group regardless of UTC offset.
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
    const existing = index.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      const group: DayGroup = {
        key,
        label: formatDate(item.session_date, 'PPPP'),
        items: [item],
      };
      groups.push(group);
      index.set(key, group);
    }
  }
  return groups;
}

export default function HistoryPage() {
  const { data, isLoading, error } = useHistory(50);
  const groups = useMemo(() => groupByLocalDay(data ?? []), [data]);

  return (
    <PageShell title="History">
      {isLoading ? (
        <p className="py-10 text-center text-sm text-[var(--color-muted)]">Loading…</p>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-[var(--color-destructive)]">
          Couldn't load your history.
        </p>
      ) : !data || data.length === 0 ? (
        <p className="mx-4 mt-4 rounded-xl bg-[var(--color-secondary-bg)] px-4 py-6 text-center text-sm text-[var(--color-muted)]">
          Your history is empty. Start a practice from the bot to see entries here.
        </p>
      ) : (
        <div className="flex flex-col gap-4 px-4 py-4">
          {groups.map((group) => (
            <section key={group.key} className="flex flex-col gap-2">
              <h2 className="sticky top-0 z-10 bg-[var(--color-bg)] py-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                {group.label}
              </h2>
              {group.items.map((card) => (
                <PracticeCard key={card.id} card={card} />
              ))}
            </section>
          ))}
        </div>
      )}
    </PageShell>
  );
}
